import { createI18n } from 'vue-i18n'
import type { I18nOptions } from 'vue-i18n'

export interface Language {
  code: string;
  name: string;
  dir: 'ltr' | 'rtl';
  dateFormat: string;
  timeFormat: string;
  numberSystem: 'latn' | 'arab';
  calendar: 'gregory' | 'islamic' | 'buddhist';
  region: string;
  pluralRules?: (n: number) => 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';
}

export interface LocaleMetadata {
  lastUpdated: string;
  translationStatus: number; // percentage complete
  maintainer: string;
}

export interface LocaleMessages {
  [key: string]: string | LocaleMessages;
}

export const SUPPORTED_LANGUAGES: Record<string, Language> = {
  en: {
    code: 'en',
    name: 'English',
    dir: 'ltr',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: 'hh:mm A',
    numberSystem: 'latn',
    calendar: 'gregory',
    region: 'US'
  },
  'en-US': {
    code: 'en-US',
    name: 'English (US)',
    dir: 'ltr',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: 'hh:mm A',
    numberSystem: 'latn',
    calendar: 'gregory',
    region: 'US'
  },
  'en-GB': {
    code: 'en-GB',
    name: 'English (UK)',
    dir: 'ltr',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: 'HH:mm',
    numberSystem: 'latn',
    calendar: 'gregory',
    region: 'GB'
  },
  ar: {
    code: 'ar',
    name: 'العربية',
    dir: 'rtl',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: 'hh:mm A',
    numberSystem: 'arab',
    calendar: 'islamic',
    region: 'SA',
    pluralRules: (n: number) => {
      if (n === 0) return 'zero'
      if (n === 1) return 'one'
      if (n === 2) return 'two'
      if (n % 100 >= 3 && n % 100 <= 10) return 'few'
      if (n % 100 >= 11 && n % 100 <= 99) return 'many'
      return 'other'
    }
  },
  ta: {
    code: 'ta',
    name: 'தமிழ்',
    dir: 'ltr',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: 'HH:mm',
    numberSystem: 'latn',
    calendar: 'gregory',
    region: 'IN'
  }
}

// Fallback chain for languages
const FALLBACK_CHAIN: Record<string, string> = {
  'en-US': 'en',
  'en-GB': 'en',
  ar: 'en',
  ta: 'en'
}

// Translation metadata
const LOCALE_METADATA: Record<string, LocaleMetadata> = {
  en: {
    lastUpdated: '2024-01-01',
    translationStatus: 100,
    maintainer: 'English Team'
  },
  'en-US': {
    lastUpdated: '2024-01-01',
    translationStatus: 100,
    maintainer: 'US Team'
  },
  'en-GB': {
    lastUpdated: '2024-01-01',
    translationStatus: 100,
    maintainer: 'UK Team'
  },
  ar: {
    lastUpdated: '2024-01-01',
    translationStatus: 100,
    maintainer: 'Arabic Team'
  },
  ta: {
    lastUpdated: '2024-01-01',
    translationStatus: 100,
    maintainer: 'Tamil Team'
  }
}

export function getLanguageFromLocale(locale: string): string {
  return locale.split('-')[0]
}

export function getBrowserLanguage(): string {
  const browserLang = navigator.language
  return SUPPORTED_LANGUAGES[browserLang] ? browserLang :
         SUPPORTED_LANGUAGES[getLanguageFromLocale(browserLang)] ? getLanguageFromLocale(browserLang) :
         'en'
}

// Analytics tracking
function trackLanguageChange(from: string, to: string) {
  // TODO: Implement analytics tracking
  console.log(`Language changed from ${from} to ${to}`)
}

// Performance monitoring
function measureLoadTime(locale: string, startTime: number) {
  const endTime = performance.now()
  console.log(`Locale ${locale} loaded in ${endTime - startTime}ms`)
}

// Async message loader with caching
const messageCache = new Map<string, LocaleMessages>()

async function loadMessages(locale: string): Promise<LocaleMessages> {
  const startTime = performance.now()

  try {
    // Check cache first
    if (messageCache.has(locale)) {
      measureLoadTime(locale, startTime)
      return messageCache.get(locale) as LocaleMessages
    }

    // const messages = await import(`../locales/${getLanguageFromLocale(locale)}.json`)
    const messages = await import(`../i18n/locales/${getLanguageFromLocale(locale)}.json`)
    messageCache.set(locale, messages.default)
    measureLoadTime(locale, startTime)
    return messages.default
  } catch (error) {
    console.error(`Failed to load messages for ${locale}`, error)
    // Try fallback language
    const fallbackLocale = FALLBACK_CHAIN[locale] || 'en'
    if (fallbackLocale !== locale) {
      return loadMessages(fallbackLocale)
    }
    return {}
  }
}

// SEO metadata generator
export function generateLanguageMetaTags(locale: string) {
  const lang = SUPPORTED_LANGUAGES[locale]
  return {
    htmlAttrs: {
      lang: locale,
      dir: lang?.dir || 'ltr'
    },
    meta: [
      {
        hid: 'content-language',
        'http-equiv': 'content-language',
        content: locale
      }
    ],
    link: Object.keys(SUPPORTED_LANGUAGES).map(code => ({
      rel: 'alternate',
      hreflang: code,
      href: `${window.location.origin}/${code}`
    }))
  }
}

// Date and number formatting configurations
const datetimeFormats = {
  'en': {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
      hour: 'numeric',
      minute: 'numeric'
    }
  },
  'ar': {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      calendar: 'islamic'
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
      hour: 'numeric',
      minute: 'numeric',
      calendar: 'islamic'
    }
  },
  'ta': {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
      hour: 'numeric',
      minute: 'numeric'
    }
  }
} as const

const numberFormats = {
  'en': {
    currency: {
      style: 'currency',
      currency: 'USD',
      currencyDisplay: 'symbol'
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    },
    percent: {
      style: 'percent',
      useGrouping: true
    }
  },
  'ar': {
    currency: {
      style: 'currency',
      currency: 'SAR',
      currencyDisplay: 'symbol',
      numberingSystem: 'arab'
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      numberingSystem: 'arab'
    },
    percent: {
      style: 'percent',
      useGrouping: true,
      numberingSystem: 'arab'
    }
  },
  'ta': {
    currency: {
      style: 'currency',
      currency: 'INR',
      currencyDisplay: 'symbol'
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    },
    percent: {
      style: 'percent',
      useGrouping: true
    }
  }
} as const

export const i18nOptions: I18nOptions = {
  legacy: false,
  locale: getBrowserLanguage(),
  fallbackLocale: 'en',
  messages: {},
  datetimeFormats,
  numberFormats,
  missingWarn: false,
  fallbackWarn: false,
  warnHtmlMessage: false
}

export const i18n = createI18n(i18nOptions)

// Helper function to set HTML dir attribute and announce direction change
export function setDocumentDirectionByLocale(locale: string) {
  const prevLocale = document.documentElement.lang
  const dir = SUPPORTED_LANGUAGES[locale]?.dir || 'ltr'
  document.documentElement.dir = dir
  document.documentElement.lang = locale

  // Track language change
  trackLanguageChange(prevLocale, locale)

  // Announce direction change for screen readers
  const announcement = document.createElement('div')
  announcement.setAttribute('role', 'status')
  announcement.setAttribute('aria-live', 'polite')
  announcement.style.position = 'absolute'
  announcement.style.width = '1px'
  announcement.style.height = '1px'
  announcement.style.padding = '0'
  announcement.style.margin = '-1px'
  announcement.style.overflow = 'hidden'
  announcement.style.clip = 'rect(0, 0, 0, 0)'
  announcement.style.whiteSpace = 'nowrap'
  announcement.style.border = '0'
  announcement.textContent = `Direction changed to ${dir === 'rtl' ? 'right to left' : 'left to right'}`
  document.body.appendChild(announcement)
  setTimeout(() => document.body.removeChild(announcement), 1000)
}

// Load messages for current locale
export async function loadLocaleMessages(locale: string) {
  const messages = await loadMessages(locale)
  i18n.global.setLocaleMessage(locale, messages)
  return messages
}

// Get translation status
export function getTranslationStatus(locale: string): LocaleMetadata {
  return LOCALE_METADATA[locale] || {
    lastUpdated: 'unknown',
    translationStatus: 0,
    maintainer: 'unknown'
  }
}

// Initialize with browser language
loadLocaleMessages(getBrowserLanguage())
